import React, { FC, MouseEvent, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import { Comment } from '@mui/icons-material';
import Accordion from '../Accordion/Accordion';
import { useTranslation } from 'react-i18next';
import CardContent from '@mui/material/CardContent';
import GrowthIndicator from '../GrowthIndicator/GrowthIndicator';
import StarRating from '../StarRating/StarRating';
import ChartComponentGmbHistory from '../ChartComponent/ChartComponentGmbHistory';
import GMBService from '../../services/GMBService';
import { IGmbLocationOpeningTime } from '../../shared/interfaces/IGmbLocationOpeningTime';
import { IGmbHistoryScore } from '../../shared/interfaces/IGmbHistoryScore';
import NumberUtils from '../../shared/NumberUtils';

interface Props {
  image?: string;
  score?: number;
  growth: number;
  locationName: string;
  reviews: number;
  locationID: string;
  addressLines?: string[];
  phoneNumber: string;
  zip: string;
  city: string;
  latitude: number;
  longitude: number;
  times: IGmbLocationOpeningTime[];
  status: string;
}

export const AccordionSummaryContentBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const LocationHistoryCard: FC<Props> = ({
  growth,
  score,
  locationName,
  reviews,
  locationID,
  addressLines,
  zip,
  city,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<FilterMode>('YEAR');
  const [scores, setScores] = useState<IGmbHistoryScore[]>([]);
  const [timesExpanded, setTimesExpanded] = useState(false);
  const [currentScore, setCurrentScore] = useState<number>(score || 0);

  const handleModeChange = (event: MouseEvent<HTMLElement>, newMode: FilterMode | null) => {
    if (newMode) {
      setMode(newMode);
    }
  };

  useEffect(() => {
    setCurrentScore(score || 0);
  }, [score]);

  useEffect(() => {
    const controller = new AbortController();

    const getScores = async () => {
      const response = await GMBService.getScores(locationID, mode, setLoading, controller.signal);
      if (response) {
        setScores(response.scores);
      }
    };

    getScores();

    return () => {
      controller.abort();
    };
  }, [locationID, mode]);

  return (
    <Accordion
      sx={{ mt: theme.spacing(1) }}
      expanded={isExpanded}
      onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
      summary={
        <>
          <AccordionSummaryContentBox sx={{ flex: '1 1 10%' }}>
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                borderRadius: `${theme.shape.borderRadius}px`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography
                variant="body2"
                sx={{ width: '31.25rem' }}
              >
                {locationName}
              </Typography>
              {addressLines !== undefined && addressLines.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {addressLines.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {addressLines.map((streetPart, index) => {
                        return (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ mr: theme.spacing(1) }}
                          >
                            {`${streetPart}, `}
                          </Typography>
                        );
                      })}
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {zip} {city}
                  </Typography>
                </Box>
              )}
            </CardContent>
            <AccordionSummaryContentBox sx={{ flex: '1 1 35%' }}>
              <StarRating value={score}></StarRating>
              <Typography
                variant="body2"
                sx={{ ml: theme.spacing(1), color: theme.palette.text.disabled }}
              >
                {NumberUtils.formatScore(currentScore)} {t('of')} 5 {t('stars')}
              </Typography>
            </AccordionSummaryContentBox>
            <AccordionSummaryContentBox sx={{ flex: '2 1 25%' }}>
              <Comment fontSize="small" />
              <Typography
                variant="body2"
                sx={{ ml: theme.spacing(1) }}
              >
                {reviews} {reviews > 1 ? `${t('reviews')}` : `${t('review')}`}
              </Typography>
            </AccordionSummaryContentBox>
            <AccordionSummaryContentBox sx={{ flex: '1 1 5%' }}>
              <GrowthIndicator growth={growth} />
              <Typography
                variant="body2"
                sx={{ minWidth: '3.75rem', ml: theme.spacing(1) }}
              >
                {growth}%
              </Typography>
            </AccordionSummaryContentBox>
          </AccordionSummaryContentBox>
        </>
      }
      details={
        <Box>
          {isExpanded && (
            <>
              <Divider sx={{ ml: theme.spacing(5) }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress
                      sx={{ ml: theme.spacing(1) }}
                      size={24}
                    />
                  </Box>
                )}
                <ToggleButtonGroup
                  value={mode}
                  color="primary"
                  size="small"
                  exclusive
                  onChange={handleModeChange}
                  aria-label="text alignment"
                  sx={{
                    my: theme.spacing(2),
                    ml: 'auto',
                    '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                      border: `1px solid ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  <ToggleButton
                    value="YEAR"
                    aria-label="YEAR"
                  >
                    YEAR
                  </ToggleButton>
                  <ToggleButton
                    value="YTD"
                    aria-label="YTD"
                  >
                    YTD
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', height: '20vh' }}>
                {scores && scores.length > 0 ? (
                  <ChartComponentGmbHistory
                    scores={scores}
                    mode={mode}
                  />
                ) : (
                  <Alert
                    severity="warning"
                    sx={{
                      minWidth: '28.125rem',
                      maxWidth: '40.625rem',
                      alignSelf: 'center',
                    }}
                  >
                    <AlertTitle sx={{ fontWeight: '700' }}>{t('no_records')}</AlertTitle>
                    {t('no_records_found')}
                  </Alert>
                )}
              </Box>
            </>
          )}
        </Box>
      }
    />
  );
};

export default LocationHistoryCard;
